import {
  ICombinedSimpleModel,
  IMultiFilterModel,
  IServerSideGetRowsRequest,
  TextFilterModel,
} from "ag-grid-community";
import { API_PAGE_LIMIT } from "apis/base.apis";
import _ from "lodash";
import { AgGridColumnFieldMapping } from "types/controls.types";
import {
  SieveSearchRequestQuery,
  SieveSortField,
  SieveFilterByField,
  SieveOrFilterByFields,
  SIEVE_FIELD_COMPARATOR,
  AdvancedSieveFilterByFields,
} from "types/sieve.types";

/**
 * Function to clean a search string for Sieve.
 * 
 * @param input the input string
 * @returns a cleaned search string that ignores duplicate spaces and trims the input
 */
export function cleanString(input: string): string {
  let trimmedString = input.trim();
  let cleanedString = trimmedString.replace(/\s+/g, ' ');
  return cleanedString;
}

export function getFilters(query?: SieveSearchRequestQuery) {
  let filters = undefined;

  if (!query) {
    return filters;
  }

  if (_.isArray(query.filters) && query.filters.length > 0) {
    filters = query.filters
      .map((filter: SieveFilterByField) => {
        let separator = filter.matchAllValues ? "," : "|";
        return `${filter.name}${filter.comparator}${cleanString(filter.values.join(separator))}`;
      })
      .join(",");
  }

  if (_.isArray(query.advancedFilters) && query.advancedFilters.length > 0) {
    filters = query.advancedFilters
      .map((advanced) => {
        const filterArray = advanced.filters.map((filter) => {
          let separator = filter.matchAllValues ? "," : "|";
          return `(${filter.name}${filter.comparator}${cleanString(filter.values.join(separator))})`;
        });

        if (advanced.type === "OR") {
          return `(${filterArray.join("|")})`;
        }

        return filterArray.join(",");
      })
      .join(",");
  }

  if (_.isArray(query.orFilters) && query.orFilters.length > 0) {
    filters += query.orFilters
      .map(
        (filter: SieveOrFilterByFields) =>
          `(${filter.names.join("|")}(${filter.comparator}${cleanString(filter.value)}`
      )
      .join(",");
  }

  return filters;
}

export function getSorts(query?: SieveSearchRequestQuery) {
  let sorts = undefined;
  if (!query) {
    return sorts;
  }

  if (_.isArray(query.sortOrders) && query.sortOrders.length > 0) {
    sorts = query.sortOrders
      .map((sort: SieveSortField) => `${sort.order === "asc" ? "" : "-"}${sort.name}`)
      .join(",");
  }

  return sorts;
}

// Only supports 'agTextColumnFilter' & "agMultiColumnFilter"
export function convertAgGridSearchToPageSize(
  request: IServerSideGetRowsRequest,
  columnMappings: Array<AgGridColumnFieldMapping>
): number {
  if (!_.isNull(request.filterModel)) {
    for (const [key, filterModel] of Object.entries(request.filterModel)) {
      const mapping = columnMappings.find((c) => c.columnName === key);
      if (!mapping) {
        continue;
      }

      // Sets page size to 0 if user selects nothing in "agMultiColumnFilter"
      if (hasMultipleFilterModels(filterModel)) {
        const multiFilterModel = filterModel as IMultiFilterModel;
        if (
          multiFilterModel.filterModels?.find((x) => {
            if (_.isEmpty(x)) {
              return false;
            }

            if ("filterType" in x && "values" in x) {
              return x.filterType === "set" && _.isEmpty(x.values);
            }

            return false;
          })
        ) {
          return 0;
        }
      }
    }
  }

  return API_PAGE_LIMIT;
}

// Only supports 'agTextColumnFilter' & "agMultiColumnFilter"
export function convertAgGridSearchToSieveSearchRequestQuery(
  request: IServerSideGetRowsRequest,
  columnMappings: Array<AgGridColumnFieldMapping>
) {
  let query: SieveSearchRequestQuery = {
    filters: [],
    orFilters: [],
    sortOrders: [],
    advancedFilters: [],
  };

  if (!_.isEmpty(request.sortModel) && request.sortModel.length > 0) {
    request.sortModel.forEach((sort) => {
      const mapping = columnMappings.find((c) => c.columnName === sort.colId);
      if (mapping) {
        query.sortOrders?.push({
          name: mapping.fieldName,
          order: sort.sort,
        });
      }
    });
  }

  if (!_.isNull(request.filterModel)) {
    for (const [key, filterModel] of Object.entries(request.filterModel)) {
      const mapping = columnMappings.find((c) => c.columnName === key);
      if (!mapping) {
        continue;
      }

      if (hasMultipleFilterModels(filterModel)) {
        const multiFilterModel = filterModel as IMultiFilterModel;
        if (multiFilterModel.filterModels)
          multiFilterModel.filterModels?.forEach((x) => {
            appendQueryFiltersFromGridFilter(query, mapping, x);
          });
      } else {
        appendQueryFiltersFromGridFilter(query, mapping, filterModel);
      }
    }
  }

  return query;
}

// Only supports 'agTextColumnFilter' & "agMultiColumnFilter"
function appendQueryFiltersFromGridFilter(
  query: SieveSearchRequestQuery,
  mapping: AgGridColumnFieldMapping,
  filterModel: any
) {
  if (!filterModel || !filterModel.filterType) {
    return;
  }

  if (filterModel.filterType === "set") {
    const values: Array<string> = filterModel.values;
    
    values.forEach((value) => {
      query.filters!.push({
        name: mapping.fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.CASE_INSENSITIVE_STRING_EQUALS,
        values: [value],
        matchAllValues: true,
      });
    });
  } else if (filterModel.filterType === "text") {
    if (instanceOfAdvancedTextFilter(filterModel)) {
      const advanced = filterModel as ICombinedSimpleModel<TextFilterModel>;
      const advancedFilters: AdvancedSieveFilterByFields = {
        filters: [],
        type: advanced.operator === "AND" ? "AND" : "OR",
      };

      advanced.conditions?.forEach((condition) => {
        const filter = convertTextFilterToQueryFilter(mapping.fieldName, condition);
        if (filter) {
          advancedFilters.filters!.push(filter);
        }
      });

      query.advancedFilters?.push(advancedFilters);
    } else {
      const basic = filterModel as TextFilterModel;
      const filter = convertTextFilterToQueryFilter(mapping.fieldName, basic);
      if (filter) {
        query.filters!.push(filter);
      }
    }
  }
}

function convertTextFilterToQueryFilter(
  fieldName: string,
  textFilter: TextFilterModel
): SieveFilterByField | undefined {
  if (textFilter.filterType !== "text") {
    return;
  }

  switch (textFilter.type) {
    case "equals":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.CASE_INSENSITIVE_STRING_EQUALS,
        values: [textFilter.filter?.trim() ?? ""],
        matchAllValues: true,
      };
    case "notEqual":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.CASE_INSENSITIVE_STRING_NOT_EQUALS,
        values: [textFilter.filter?.trim() ?? ""],
        matchAllValues: true,
      };
    case "contains":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.CASE_INSENSITIVE_STRING_CONTAINS,
        values: [textFilter.filter?.trim() ?? ""],
        matchAllValues: true,
      };
    case "notContains":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.CASE_INSENSITIVE_STRING_DOES_NOT_CONTAINS,
        values: [textFilter.filter?.trim() ?? ""],
        matchAllValues: true,
      };
    case "startsWith":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.CASE_INSENSITIVE_STRING_BEGINS_WITH,
        values: [textFilter.filter?.trim() ?? ""],
        matchAllValues: true,
      };
    case "endsWith":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.ENDS_WITH,
        values: [textFilter.filter?.trim() ?? ""],
        matchAllValues: true,
      };
    case "blank":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.EQUALS,
        values: ["null"],
        matchAllValues: false,
      };
    case "notBlank":
      return {
        name: fieldName,
        comparator: SIEVE_FIELD_COMPARATOR.NOT_EQUALS,
        values: ["null"],
        matchAllValues: true,
      };
  }

  return;
}

function instanceOfAdvancedTextFilter(
  object: any
): object is ICombinedSimpleModel<TextFilterModel> {
  return "conditions" in object && object.filterType === "text";
}

function hasMultipleFilterModels(object: any): object is IMultiFilterModel {
  return "filterModels" in object && object.filterType === "multi";
}
